import React from "react";
import {
    BrokerReportAssignmentLine,
    BrokerReportForemanGroup,
    BrokerReportGroup,
    BrokerReportJobGroup
} from "../../types/BrokerReportGroup";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {DateUtility} from "../common/DateUtility";
import {LocationUtility} from "../common/LocationUtility";
import {PhoneUtility} from "../common/PhoneUtility";

interface BrokerPlanAssignmentPart {
    text: string;
    modifierClassName: string;
    showsRoundRobinIcon?: boolean;
}

interface BrokerPlanContentProps {
    // a day the broker has no assignments on returns no group at all, which is why the plan renders every level defensively
    brokerGroup?: BrokerReportGroup;
    reportDate: string;
    generatedAt: string;
}

// The plan states the direction as plain text, without the pointer image the admin's ActivityDirectionDisplay carries.
let planDirectionWords: {[directionName: string]: string} = {
    [Constants.activityDirectionNames.inbound]: 'Inbound',
    [Constants.activityDirectionNames.outbound]: 'Outbound',
};

function hasText(textValue: string) {
    return !!textValue;
}

function hasPartText(assignmentPart: BrokerPlanAssignmentPart) {
    return !!assignmentPart.text;
}

export let BrokerPlanContent = function (props: BrokerPlanContentProps) {
    // each row's parts carry a leading separator so the desktop styles read the two rows back as one pipe-separated line; only the line's very first part goes without one
    function renderAssignmentPartNodes(assignmentParts: BrokerPlanAssignmentPart[], rowName: string, showsLeadingSeparator: boolean) {
        let assignmentPartNodes: React.ReactNode[] = [];
        for (let i = 0; i < assignmentParts.length; i++) {
            let roundRobinIcon = undefined;
            if (assignmentParts[i].showsRoundRobinIcon) {
                roundRobinIcon = <img
                    src="/static/images/round-robin-light-gray-on-textcolor.svg"
                    className="broker-plan-assignment-round-robin-icon"
                />
            }
            if (i > 0 || showsLeadingSeparator) {
                assignmentPartNodes.push(
                    <div key={`plan-assignment-${rowName}-separator-${i}`} className="broker-plan-assignment-separator">|</div>
                );
            }
            assignmentPartNodes.push(
                <div
                    key={`plan-assignment-${rowName}-part-${i}`}
                    className={`broker-plan-assignment-part${assignmentParts[i].modifierClassName}`}
                >
                    {assignmentParts[i].text}
                    {roundRobinIcon}
                </div>
            );
        }
        return assignmentPartNodes;
    }

    function renderAssignment(assignmentLine: BrokerReportAssignmentLine) {
        let activity = assignmentLine.activity;
        let assignedMaterial = assignmentLine.assigned_activity_material;
        // the plan drops a material piece the record is missing rather than writing "undefined" into the line
        let materialPieces = assignedMaterial
            ? [
                assignmentLine.assigned_material_amount != null ? '' + assignmentLine.assigned_material_amount : '',
                assignedMaterial.measurement_unit ? assignedMaterial.measurement_unit + 's' : '',
                assignedMaterial.name || ''
            ]
            : [];
        let firstRowParts = [
            {
                text: planDirectionWords[activity?.activity_direction?.name || ''] || '',
                modifierClassName: ' direction',
            },
            {
                text: assignmentLine.truck_count != null ? String('' + assignmentLine.truck_count).padStart(2, '0') + ' trucks' : '',
                modifierClassName: '',
                showsRoundRobinIcon: !!assignmentLine.is_round_robin,
            },
            {
                text: DateUtility.dbDateToDisplayTime(assignmentLine.start_time || activity?.activity_start_time || ''),
                modifierClassName: '',
            },
        ].filter(hasPartText);
        // the plan keeps the material's stored capitals, unlike the Initial Caps the activity sub-rows apply
        let secondRowParts = [
            {text: materialPieces.filter(hasText).join(' '), modifierClassName: ''},
            {
                text: activity?.purchase_order_number ? 'PO ' + activity.purchase_order_number : '',
                modifierClassName: '',
            },
        ].filter(hasPartText);

        return (
            <div className="broker-plan-assignment" key={`plan-assignment-${assignmentLine.id}`}>
                <div className="broker-plan-assignment-line">
                    <div className="broker-plan-assignment-line-row">
                        {renderAssignmentPartNodes(firstRowParts, 'first-row', false)}
                    </div>
                    <ConditionalRenderer
                        if={secondRowParts.length}
                        renderer={() => (
                            <div className="broker-plan-assignment-line-row">
                                {renderAssignmentPartNodes(secondRowParts, 'second-row', firstRowParts.length > 0)}
                            </div>
                        )}
                    />
                </div>
                <ConditionalRenderer
                    if={assignmentLine.broker_notes}
                    renderer={() => (
                        <div className="broker-plan-assignment-instructions">{assignmentLine.broker_notes}</div>
                    )}
                />
            </div>
        );
    }

    function renderJob(jobGroup: BrokerReportJobGroup) {
        return (
            <div className="broker-plan-job" key={`plan-job-${jobGroup.job?.id}`}>
                <div className="broker-plan-job-header">
                    <div className="broker-plan-job-number">{jobGroup.job?.job_number}</div>
                    <div className="broker-plan-job-name">{jobGroup.job?.name}</div>
                    <div className="broker-plan-job-location">
                        {jobGroup.job?.location ? LocationUtility.locationLabel(jobGroup.job.location) : ''}
                    </div>
                </div>
                <div className="broker-plan-assignments">
                    {(jobGroup.dispatchOrderSections || []).map(function (dispatchOrderSection) {
                        return (dispatchOrderSection.activity_broker_assignments || []).map(renderAssignment);
                    })}
                </div>
            </div>
        );
    }

    function renderForemanSection(foremanGroup: BrokerReportForemanGroup) {
        let foremanPhone = PhoneUtility.dbPhoneToParenthesesDisplayPhone(foremanGroup.foreman?.person?.phone);
        return (
            <div className="broker-plan-foreman-section" key={`plan-foreman-section-${foremanGroup.foreman?.id}`}>
                <div className="broker-plan-foreman-header">
                    <div className="broker-plan-foreman-name">{foremanGroup.foreman?.person?.name}</div>
                    <ConditionalRenderer
                        if={foremanPhone}
                        renderer={() => (
                            <a className="broker-plan-foreman-phone" href={"tel:" + foremanPhone}>{foremanPhone}</a>
                        )}
                    />
                </div>
                <div className="broker-plan-jobs">
                    {(foremanGroup.jobGroups || []).map(renderJob)}
                </div>
            </div>
        );
    }

    return (
        <div className="broker-plan-content">
            <div className="broker-plan-header">
                <img
                    src="/static/images/berger-logo-full-slate.svg"
                    className="broker-plan-logo"
                />
                <div className="broker-plan-title-row">
                    <div className="broker-plan-broker-name">{props.brokerGroup?.broker?.name}</div>
                    <div className="broker-plan-title">
                        Requested Trucks for {DateUtility.dbDateToBrokerPlanTitleDate(props.reportDate)}
                    </div>
                </div>
                <div className="broker-plan-generated-line">
                    Generated {DateUtility.dbDateToBrokerPlanGeneratedDateTime(props.generatedAt)}
                </div>
            </div>
            <div className="broker-plan-foreman-sections">
                {(props.brokerGroup?.foremanGroups || []).map(renderForemanSection)}
            </div>
            <div className="broker-plan-footer">
                <div className="broker-plan-footer-text">2026 | Berger Contractors</div>
                <div className="broker-plan-footer-text">
                    Contact Dispatch<span className="broker-plan-footer-desktop-words"> for any questions</span>: <a className="broker-plan-footer-phone-link" href="tel:224.370.6378">224.370.6378</a>
                </div>
            </div>
        </div>
    );
};
